/**
 * speechRecognition — thin wrapper over the Web Speech API for
 * one-shot voice transcription. Hides browser-prefixing
 * (window.SpeechRecognition vs window.webkitSpeechRecognition) and
 * degrades gracefully when the API is unavailable.
 *
 * Used by RoomDiscoveryPicker for the optional mic affordance: cleaner
 * taps the mic, says "two thirteen", the transcript flows through
 * parseRoomNumber and pre-fills the room number field. Falls back to
 * typing on any failure (no support, mic denied, network error).
 */

interface SpeechRecognitionEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string }>>
}

interface SpeechRecognitionErrorEventLike {
  error?: string
}

interface SpeechRecognitionInstance {
  start: () => void
  stop:  () => void
  abort: () => void
  lang:           string
  continuous:     boolean
  interimResults: boolean
  onresult: (e: SpeechRecognitionEventLike) => void
  onerror:  (e: SpeechRecognitionErrorEventLike) => void
  onend:    () => void
}

interface SpeechRecognitionWindow {
  SpeechRecognition?:       new () => SpeechRecognitionInstance
  webkitSpeechRecognition?: new () => SpeechRecognitionInstance
}

function getSpeechCtor(): (new () => SpeechRecognitionInstance) | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as SpeechRecognitionWindow
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export function isSpeechRecognitionSupported(): boolean {
  return getSpeechCtor() !== null
}

export interface StartListeningOptions {
  onResult: (transcript: string) => void
  onError:  (code: string) => void
  onEnd?:   () => void
  /** BCP-47 lang tag. Defaults to 'en-US'. */
  lang?:    string
}

/**
 * Starts a one-shot recognition. Returns a stop() function on success,
 * or null when speech recognition is not supported / failed to start.
 * Callers should call the stop() function on cleanup; the recognizer
 * also auto-stops after one result via onend.
 */
export function startListening(opts: StartListeningOptions): (() => void) | null {
  const Ctor = getSpeechCtor()
  if (!Ctor) return null

  const rec = new Ctor()
  rec.lang           = opts.lang ?? 'en-US'
  rec.continuous     = false
  rec.interimResults = false

  rec.onresult = (event) => {
    const first = event.results?.[0]?.[0]
    const transcript = (first?.transcript ?? '').trim()
    if (transcript.length === 0) {
      opts.onError('NO_TRANSCRIPT')
      return
    }
    opts.onResult(transcript)
  }
  rec.onerror = (event) => {
    opts.onError(event.error ?? 'UNKNOWN')
  }
  rec.onend = () => {
    opts.onEnd?.()
  }

  try {
    rec.start()
  } catch {
    opts.onError('START_FAILED')
    return null
  }
  return () => {
    try { rec.stop() } catch { /* recognizer already stopped */ }
  }
}
